import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';

// MUI Icons
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Local Components
import FieldSection from './FieldSection';

// Types
import { ControlModes } from '@src/types/Stacks';

// Context
import { useStackModalContext } from '../context/StackModalContext';

// Styles
import {
  accordionDetailsStyles,
  accordionStyles,
  accordionSummaryStyles,
  errorTextStyles,
  listItemStyles,
  textFieldStyles,
} from './ControlModeItem.styles';

// Component Props
interface ControlModeItemProps {
  controlMode: ControlModes;
  index: number;
}

// Sub-components
const ControlModeHeader: React.FC<{
  controlMode: ControlModes;
  index: number;
}> = ({ controlMode, index }) => {
  const {
    state: { validationErrors },
    actions: {
      controlMode: {
        handleControlModeAliasChange,
        handleControlModeEnabledChange,
      },
    },
  } = useStackModalContext();
  const [hasAlias, setHasAlias] = React.useState(!!controlMode.stackAlias);

  return (
    <Grid2 container={true} spacing={1} sx={{ mb: 2 }} size={{ xs: 12 }}>
      <Grid2
        size={{
          xs: 12,
        }}
        container
        spacing={1}
        alignItems='center'
        sx={{ mb: 1 }}
      >
        <TextField
          label='Control Mode Name'
          value={controlMode.name}
          fullWidth
          margin='dense'
          error={!!validationErrors[`controlMode_${index}_name`]}
          helperText={
            validationErrors[`controlMode_${index}_name`] ||
            'Control Mode Name can only be edited on the Control Mode Templates page'
          }
          sx={textFieldStyles}
          variant='outlined'
          size='small'
          disabled={true}
          slotProps={{
            input: {
              readOnly: true,
            },
          }}
        />
      </Grid2>

      <Grid2
        size={{
          xs: 12,
        }}
        container
        spacing={1}
        alignItems='center'
        sx={{ mb: 1 }}
      >
        <Grid2 size={{ xs: 12, sm: 8 }}>
          <TextField
            label='Control Mode Stack Alias'
            value={controlMode.stackAlias || ''}
            onChange={(e) =>
              handleControlModeAliasChange(index, e.target.value)
            }
            fullWidth
            margin='dense'
            disabled={!hasAlias}
            error={!!validationErrors[`controlMode_${index}_stackAlias`]}
            helperText={validationErrors[`controlMode_${index}_stackAlias`]}
            sx={textFieldStyles}
            variant='outlined'
            size='small'
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 4 }}>
          <FormControlLabel
            control={
              <Switch
                checked={hasAlias}
                onChange={() => setHasAlias(!hasAlias)}
                color='primary'
                title={
                  controlMode.enabled
                    ? 'Disable adding control mode stack alias'
                    : 'Enable adding control mode stack alias'
                }
              />
            }
            label={
              <Typography variant='body2' fontWeight='medium'>
                Enable Alias
              </Typography>
            }
          />
        </Grid2>
      </Grid2>

      <Grid2>
        <FormControlLabel
          control={
            <Switch
              checked={controlMode.enabled}
              onChange={(e) =>
                handleControlModeEnabledChange(index, e.target.checked)
              }
              color='primary'
              title={
                controlMode.enabled
                  ? 'Disable this control mode'
                  : 'Enable this control mode'
              }
            />
          }
          label={
            <Typography
              variant='body2'
              fontWeight='medium'
              title={
                controlMode.enabled
                  ? 'This control mode is currently enabled and will be used'
                  : 'This control mode is currently disabled and will not be used'
              }
            >
              Enabled
            </Typography>
          }
        />
      </Grid2>

      {validationErrors[`controlMode_${index}_sections`] && (
        <Typography sx={errorTextStyles}>
          {validationErrors[`controlMode_${index}_sections`]}
        </Typography>
      )}
    </Grid2>
  );
};

const ControlModeActions: React.FC<{
  controlMode: ControlModes;
  index: number;
}> = ({ index }) => {
  const {
    actions: {
      template: { handleOpenTemplatePicker },
      controlMode: { handleDuplicateControlMode, handleOpenDeleteConfirm },
    },
  } = useStackModalContext();

  return (
    <Grid2 container alignItems='center' spacing={1} justifyContent='flex-end'>
      <Grid2>
        <Button
          variant='outlined'
          size='small'
          color='primary'
          onClick={() => handleOpenTemplatePicker(index)}
          aria-label={'Select Templates'}
          data-testid={'select-templates-button'}
          sx={{
            borderRadius: 1,
            px: 2,
            fontWeight: 'medium',
            textTransform: 'none',
            minWidth: 130,
          }}
        >
          Select Templates
        </Button>
      </Grid2>
      <Grid2>
        <IconButton
          onClick={() => handleDuplicateControlMode(index)}
          title='Duplicate control mode'
          aria-label='Duplicate control mode'
          size='small'
          sx={{
            opacity: 0.4,
          }}
        >
          <ContentCopyIcon fontSize='small' />
        </IconButton>
      </Grid2>
      <Grid2>
        <IconButton
          onClick={() => handleOpenDeleteConfirm(index)}
          title='Remove control mode'
          aria-label='Remove control mode'
          color='error'
          size='small'
          sx={{
            color: 'error.light',
            opacity: 0.4,
            '&:hover': {
              opacity: 1,
            },
          }}
        >
          <DeleteIcon />
        </IconButton>
      </Grid2>
    </Grid2>
  );
};

const ControlModeSections: React.FC<{
  controlMode: ControlModes;
  index: number;
}> = ({ controlMode, index }) => {
  const {
    state: { validationErrors },
    actions: {
      field: { handleFieldChange, handleDeleteSection },
    },
  } = useStackModalContext();

  return controlMode.sections?.length > 0 ? (
    <Grid2 size={12} sx={{ px: 1 }}>
      <Accordion
        sx={accordionStyles}
        defaultExpanded={false}
        id={`control-mode-${index}-sections-accordion`}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={accordionSummaryStyles}
          aria-label='Toggle field editor'
        >
          <Grid2 container spacing={1} alignItems='center'>
            <Grid2>
              <Typography
                sx={{ fontWeight: 'medium' }}
                title='Click to expand and edit field values for this control mode'
              >
                Edit Fields
              </Typography>
            </Grid2>
            <Grid2>
              <Typography
                component='span'
                variant='caption'
                sx={{ ml: 1, color: 'text.secondary', fontWeight: 'normal' }}
              >
                ({controlMode.sections.length}{' '}
                {controlMode.sections.length === 1 ? 'section' : 'sections'})
              </Typography>
            </Grid2>
          </Grid2>
        </AccordionSummary>
        <AccordionDetails sx={accordionDetailsStyles}>
          {controlMode.sections.map((section, sectionIndex: number) => (
            <FieldSection
              key={sectionIndex}
              section={section}
              sectionIndex={sectionIndex}
              controlModeIndex={index}
              onFieldChange={(
                sectionIndex: number,
                fieldIndex: number,
                newValue: string | number | boolean | null,
              ) => handleFieldChange(index, sectionIndex, fieldIndex, newValue)}
              onDeleteSection={(sectionIndex: number) =>
                handleDeleteSection(index, sectionIndex)
              }
              validationErrors={validationErrors}
            />
          ))}
        </AccordionDetails>
      </Accordion>
    </Grid2>
  ) : null;
};

// Main Component
const ControlModeItem: React.FC<ControlModeItemProps> = ({
  controlMode,
  index,
}) => {
  return (
    <Box sx={{ mb: 3 }}>
      <Grid2 container spacing={1} sx={listItemStyles}>
        <Grid2 size={8}>
          <ControlModeHeader controlMode={controlMode} index={index} />
        </Grid2>
        <Grid2 size={4} sx={{ mt: 1.3 }}>
          <ControlModeActions controlMode={controlMode} index={index} />
        </Grid2>

        <ControlModeSections controlMode={controlMode} index={index} />
      </Grid2>

      <Divider sx={{ my: 2 }} />
    </Box>
  );
};

export default React.memo(ControlModeItem);
